import type { ComponentProps } from 'react';

import { getConversationListRowLoadingStatus } from '@atlassian/conversation-assistant-ui-components/utils/conversation-task-status-indicator';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';
import type { SessionListItem } from '@atlassian/rovo-platform-ui-components/session-list/SessionListItem';

import type { Conversation } from '../../../common/types';
import type { NavigationAgentSession } from '../NavigationAgentSessionsContext';
import { getSessionLastMessageTimestamp } from './getSessionLastMessageTimestamp';
import { getSessionListItemContexts } from './getSessionListItemContexts';

type SessionListItemAdapterOptions = Readonly<{
	rovoAgentName: string;
	name?: string;
	session?: NavigationAgentSession;
}>;

/** Maps the conversation store model into the normalized data rendered by SessionListItem. */
export const mapConversationToSessionListSession = (
	conversation: Conversation,
	{ rovoAgentName, name = conversation.name, session }: SessionListItemAdapterOptions,
): ComponentProps<typeof SessionListItem>['session'] => {
	const rowStatus = getConversationListRowLoadingStatus(
		conversation,
		conversation.readStatus === false,
	);
	const state =
		session?.state ?? (rowStatus === 'loading' ? 'WORKING' : conversation.agentSessionState);
	const lastStateChangeAt = getSessionLastMessageTimestamp(conversation, session);
	const isRovo = Boolean(
		conversation.agent?.is_default ||
		isDefaultRovoChat({
			agentId: conversation.agent?.agent_id,
			agentNamedId: conversation.agent?.agent_named_id,
		}),
	);
	const displayAgent = conversation.agent
		? {
				id: conversation.agent.agent_id ?? conversation.agent.agent_named_id ?? 'agent',
				name: isRovo ? rovoAgentName : (conversation.agent.agent_name ?? ''),
				isRovo,
				agentId: conversation.agent.agent_id,
				agentIdentityAccountId: conversation.agent.identity_account_id,
				agentNamedId: conversation.agent.agent_named_id,
				...(session?.displayAgent?.avatarUrl ? { avatarUrl: session.displayAgent.avatarUrl } : {}),
			}
		: undefined;
	const contexts =
		rowStatus === undefined ? getSessionListItemContexts(conversation.scopes) : undefined;

	return {
		id: conversation.id,
		conversationChannel: {
			id: conversation.id,
			name,
			readStatus: session?.conversationChannel?.readStatus ?? conversation.readStatus,
		},
		state,
		displayAgent,
		contexts,
		lastStateChangeAt,
	};
};
